/**
 * Phase A2 — Employer password sign-in at `/employer-signin`.
 *
 * Replaces the magic-link-by-default flow. POSTs to /api/employer/signin
 * with email + password + remember_me. On 200 we navigate to the server-
 * provided `redirect_to` (typically `/portal`). On 401 `invalid_credentials`
 * we show the warm error copy. On 401 `password_not_set` we show the
 * "we need to set you up" copy (A4 wires the setup email). On 429
 * `locked_out` we show the rate-limit copy.
 *
 * The old /sent surface, BroadcastChannel coordination, and 5s polling
 * are retired here. Existing magic-link endpoints still work; they're
 * just no longer reachable from this page. Deep-link error params from
 * magic-link 302s (err=invalid_link|already_used|expired) are still
 * surfaced as a banner above the form — users arriving here from an
 * expired link still get a clear message.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { track } from "../lib/analytics";
import {
    employerPasswordSignin,
    formatApiError,
    passwordSetupEmail,
} from "../lib/api";
import BackLink from "../components/BackLink";
import { CALENDLY_SUCCESS_TEAM_URL } from "../config/constants";
import { isValidEmail } from "../lib/dataHygiene";
import "./Team.css";
import "./SignIn.css";

const MAGIC_LINK_ERROR_KEYS = ["invalid_link", "already_used", "expired"];

export default function EmployerSignIn() {
    const { t, i18n } = useTranslation();
    const [params] = useSearchParams();

    const [email, setEmail] = useState(params.get("email") || "");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(""); // one of "" | "invalid" | "locked" | "password_not_set" | "network"
    const [lockUntilSeconds, setLockUntilSeconds] = useState(0);
    const emailRef = useRef(null);

    const urlErr = params.get("err");
    const bannerKey = urlErr && MAGIC_LINK_ERROR_KEYS.includes(urlErr)
        ? `auth.employerSignin.err.${urlErr}`
        : null;

    useEffect(() => {
        track("signin_view", { audience: "employer", channel: "password" });
        document.title = t("auth.employerSignin.title");
    }, [t]);

    // Autofocus on email unless it's prefilled (from ?email=...); in
    // that case jump to the password field so the user can just type.
    useEffect(() => {
        if (emailRef.current) {
            if (email) {
                const pw = document.querySelector("[data-testid=employer-signin-password]");
                pw && pw.focus();
            } else {
                emailRef.current.focus();
            }
        }
        // Intentionally once on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const emailValid = useMemo(() => isValidEmail(email.trim()), [email]);
    const canSubmit = emailValid && password.length >= 1 && !submitting;

    async function handleSubmit(e) {
        e.preventDefault();
        if (!canSubmit) return;
        setErr("");
        setSubmitting(true);
        try {
            const res = await employerPasswordSignin({
                email: email.trim(),
                password,
                rememberMe,
            });
            track("signin_submit", {
                audience: "employer",
                channel: "password",
                remember_me: rememberMe,
                outcome: "ok",
            });
            const dest = res?.redirect_to || "/portal";
            window.location.replace(dest);
        } catch (error) {
            const status = error?.response?.status;
            const detail = error?.response?.data?.detail;
            if (status === 429 && detail === "locked_out") {
                setErr("locked");
                setLockUntilSeconds(
                    error.response.data?.retry_after_seconds || 15 * 60,
                );
                track("signin_submit", {
                    audience: "employer",
                    outcome: "locked_out",
                });
            } else if (status === 401 && detail === "password_not_set") {
                setErr("password_not_set");
                // Phase A4 — fire a setup-email so the user gets a
                // create-password link. Enumeration-safe on the server;
                // if the call itself throws we still show the banner.
                try {
                    await passwordSetupEmail({
                        role: "employer",
                        email: email.trim(),
                        locale: i18n?.language?.startsWith("es") ? "es" : "en",
                    });
                    track("password_setup_email_sent", { audience: "employer" });
                } catch (_) {
                    // no-op — banner copy tells them to check their inbox
                }
                track("signin_submit", {
                    audience: "employer",
                    outcome: "password_not_set",
                });
            } else if (status === 401) {
                setErr("invalid");
                track("signin_submit", {
                    audience: "employer",
                    outcome: "invalid_credentials",
                });
            } else {
                setErr("network");
                track("signin_submit", {
                    audience: "employer",
                    outcome: "error",
                    message: formatApiError(error),
                });
            }
        } finally {
            setSubmitting(false);
        }
    }

    const errorCopyKey = {
        invalid: "auth.signin.errors.invalidCredentials",
        locked: "auth.signin.errors.lockedOut",
        password_not_set: "auth.signin.errors.passwordNotSet",
        network: "auth.signin.errors.network",
    }[err];

    return (
        <section className="lg-section team-track" data-testid="employer-signin-page">
            <div className="team-signin-backrow">
                <BackLink to="/" testId="employer-signin-back" />
            </div>

            <div className="team-panel">
                <p className="section-label mono">
                    {t("auth.employerSignin.eyebrow")}
                </p>
                <h1 className="team-title">
                    {t("auth.employerSignin.title")}
                </h1>
                <p className="team-sub">{t("auth.employerSignin.sub")}</p>

                {bannerKey && (
                    <div
                        className="apply-err"
                        data-testid="employer-signin-link-error-banner"
                        role="status"
                    >
                        {t(bannerKey)}
                    </div>
                )}

                <form
                    className="signin-form"
                    onSubmit={handleSubmit}
                    autoComplete="on"
                    data-testid="employer-signin-form"
                    noValidate
                >
                    <div
                        className="signin-error-live"
                        aria-live="polite"
                        data-testid="employer-signin-error"
                    >
                        {errorCopyKey && (
                            <div className="signin-error">
                                {t(errorCopyKey, {
                                    minutes: Math.ceil((lockUntilSeconds || 900) / 60),
                                })}
                            </div>
                        )}
                    </div>

                    <label className="signin-label mono" htmlFor="employer-signin-email">
                        {t("auth.signin.emailLabel")}
                    </label>
                    <input
                        ref={emailRef}
                        id="employer-signin-email"
                        type="email"
                        name="email"
                        autoComplete="username"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="signin-input"
                        data-testid="employer-signin-email"
                    />

                    <label className="signin-label mono" htmlFor="employer-signin-password">
                        {t("auth.signin.passwordLabel")}
                    </label>
                    <input
                        id="employer-signin-password"
                        type="password"
                        name="password"
                        autoComplete="current-password"
                        required
                        minLength={1}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="signin-input"
                        data-testid="employer-signin-password"
                    />

                    <label className="signin-remember">
                        <input
                            type="checkbox"
                            name="remember_me"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            data-testid="employer-signin-remember-me"
                        />
                        <span>{t("auth.signin.rememberLabel")}</span>
                    </label>

                    <button
                        type="submit"
                        className="signin-submit"
                        disabled={!canSubmit}
                        data-testid="employer-signin-submit"
                    >
                        {submitting
                            ? t("auth.signin.submitting")
                            : t("auth.signin.submit")}
                    </button>

                    <div className="signin-ancillary">
                        <Link
                            to={`/forgot-password?role=employer${email ? `&email=${encodeURIComponent(email)}` : ""}`}
                            className="signin-forgot"
                            data-testid="employer-signin-forgot"
                        >
                            {t("auth.signin.forgot")}
                        </Link>
                    </div>
                </form>

                <div className="signin-secondary" data-testid="employer-signin-secondary">
                    <Link
                        to="/apply"
                        className="signin-secondary-link"
                        data-testid="employer-signin-go-to-apply"
                    >
                        {t("auth.employerSignin.goToApply")}
                    </Link>
                    <a
                        href={CALENDLY_SUCCESS_TEAM_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="signin-secondary-link"
                        data-testid="employer-signin-contact"
                    >
                        {t("auth.signin.errors.contactUs")}
                    </a>
                </div>
            </div>
        </section>
    );
}
